import { useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useAdminTheme } from '../lib/AdminTheme';
import AdminThemeToggle from './AdminThemeToggle';
import { LayoutDashboard, ShoppingBag, Package, CreditCard, Settings, ArrowLeft, Truck, Activity, ScrollText, FileText, CalendarDays } from 'lucide-react';

const NAV = [
  { to: '/ops', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/ops/orders', label: 'Orders', icon: ShoppingBag },
  { to: '/ops/inventory', label: 'Inventory', icon: Package },
  { to: '/ops/payments', label: 'Payments', icon: CreditCard },
  { to: '/ops/shipments', label: 'Shipments', icon: Truck },
  { to: '/ops/accounting', label: 'Accounting', icon: FileText, end: true },
  { to: '/ops/accounting/weeks', label: 'Weekrapport', icon: CalendarDays },
  { to: '/ops/active-ants', label: 'Active Ants', icon: Activity },
  { to: '/ops/logs', label: 'Logs', icon: ScrollText },
  { to: '/ops/system', label: 'System', icon: Settings },
];

export default function OpsLayout() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { preference, effectiveTheme, setPreference } = useAdminTheme();

  useEffect(() => {
    if (isLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') navigate('/');
  }, [user, isLoading, navigate]);

  if (isLoading || !user || user.role !== 'admin') return null;

  // `dark` class + `data-theme` attribute are scoped to this layout
  // root ONLY. Storefront never sees them. Inside the wrapper the
  // shadcn HSL variables flip and every `bg-background`, `bg-card`,
  // `text-foreground` automatically adapts.
  return (
    <div data-testid="ops-layout" data-theme={effectiveTheme}
      className={`${effectiveTheme === 'dark' ? 'dark' : ''} flex min-h-[calc(100vh-64px)] bg-background`}>
      <aside className="hidden md:flex w-52 shrink-0 border-r border-border flex-col" style={{ background: '#1a1f2e' }}>
        <div className="px-5 py-5 border-b border-white/10">
          <p className="text-white text-lg font-semibold tracking-wide" style={{ fontFamily: 'var(--font-sans)' }}>Ops Center</p>
          <p className="text-white/40 text-xs mt-0.5">The Herborist</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`ops-nav-${to.split('/').pop() || 'dashboard'}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive ? 'bg-white/15 text-white' : 'text-white/50 hover:bg-white/8 hover:text-white/80'
                }`
              }>
              <Icon className="w-4 h-4 shrink-0" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10 space-y-2">
          <AdminThemeToggle
            preference={preference}
            effectiveTheme={effectiveTheme}
            onChange={setPreference}
            variant="ops"
          />
          <NavLink to="/admin/products"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Commerce Admin
          </NavLink>
        </div>
      </aside>
      <main className="flex-1 overflow-auto bg-[#f8f9fb] dark:bg-background text-foreground">
        <Outlet />
      </main>
    </div>
  );
}
